import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Point } from '@angular/cdk/drag-drop';
import type { OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { Overlay } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Directive, HostListener, Input, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { fromEvent, merge, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';


@Directive({
  selector: '[muiContextMenu]',
  providers: [MuiContextMenuDirective]
})
export class MuiContextMenuDirective implements OnDestroy {

  @Input('muiContextMenu') contextMenu?: TemplateRef<unknown>;
  @Input() touchOpenDelay = 1000;

  @Input()
  set openOnClick(val: boolean) {
    this._openOnClick = coerceBooleanProperty(val);
  }

  get openOnClick(): boolean {
    return this._openOnClick;
  }

  get isOpen(): boolean {
    return !!this._overlayRef;
  }

  ngOnDestroy(): void {
    this.close();
  }

  private _openOnClick = false;
  private _overlayRef: OverlayRef | null = null;
  private _clickOutsideSubscription: Subscription | null = null;
  private _openTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly _clickOutside = merge(
    fromEvent(document, 'mousedown'),
    fromEvent(document, 'touchstart'),
    fromEvent(document, 'contextmenu')
  ).pipe(
    filter(e => {
      if (!this._overlayRef) {
        return true;
      }

      let current = e.target as HTMLElement;
      while (current.parentElement) {
        if (current === this._overlayRef.overlayElement) {
          return false;
        }
        current = current.parentElement;
      }

      return true;
    }),
    take(1)
  );

  constructor(public overlay: Overlay, public viewContainerRef: ViewContainerRef) {
  }

  @HostListener('contextmenu', ['$event'])
  @HostListener('touchstart', ['$event'])
  @HostListener('click', ['$event'])
  onMouseDown(event: MouseEvent | TouchEvent): void {
    event.stopPropagation();
    if (event.type === 'contextmenu' || (event.type === 'click' && this._openOnClick)) {
      this.open(event);
    } else if (event.type === 'touchstart') {
      this._openTimeout = setTimeout(() => {
        this.open(event);
      }, this.touchOpenDelay);
    }
  }

  @HostListener('touchend')
  @HostListener('mouseup')
  endPress(): void {
    if (this._openTimeout) {
      clearTimeout(this._openTimeout);
      this._openTimeout = null;
    }
  }

  open(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.contextMenu) {
      return;
    }

    this.close();

    let coords: Point = { x: 0, y: 0 };

    if ('clientX' in event) {
      coords = { x: event.clientX, y: event.clientY };
    } else if ('touches' in event) {
      coords = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }

    const scrollStrategy = this.overlay.scrollStrategies.close({ threshold: 10 });
    const positionStrategy: PositionStrategy = this.overlay.position()
      .flexibleConnectedTo({ x: coords.x + 5, y: coords.y + 5 })
      .withPositions([
        { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
        { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' },
      ])
      .withGrowAfterOpen(true)
      .withViewportMargin(5)
      .withPush(true);

    // Create the overlay and attach to the view container
    this._overlayRef = this.overlay.create({
      panelClass: 'mui-context-menu',
      disposeOnNavigation: true,
      scrollStrategy,
      positionStrategy
    });

    this._overlayRef.overlayElement.setAttribute('role', 'menu');
    this._overlayRef.attach(new TemplatePortal(this.contextMenu, this.viewContainerRef, {
      close: (): void => this.close()
    }));

    this._clickOutsideSubscription = this._clickOutside.subscribe(() => this.close());

    // The position strategy needs to be applied after the overlay has been attached and mounted, otherwise it can be positioned
    // outside the viewport.
    setTimeout(() => {
      positionStrategy.apply();
      // this._overlayRef should be defined at this point, but TS doesn't know that
      this._overlayRef?.overlayElement.setAttribute('data-positioned', 'true');
    });
  }

  close(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;
    }

    if (this._clickOutsideSubscription) {
      this._clickOutsideSubscription.unsubscribe();
      this._clickOutsideSubscription = null;
    }
  }
}
